/**
 * Popbar Flavor Picker — Client Wiring
 * =====================================
 * Prestige 4.5.5-compatible flavor picker for the 6 pack products.
 *
 * Responsibilities:
 *   - Load flavor manifest from inline bootstrap JSON
 *   - Filter flavors by product type (gelato-only / sorbetto-only / mixed)
 *   - Handle Select mode + Surprise Me mode (1 reshuffle limit)
 *   - Populate hidden LIP inputs on selection change
 *   - Trigger companion SKU add on Add to Cart (Path B pricing model)
 *   - Emit theme.pubsub events for GTM/Brevo analytics
 *   - Detect fulfillment context from Pickeasy _instoreAppId presence
 */

(function() {
  'use strict';

  // ---------------------------------------------------------------------------
  // Configuration
  // ---------------------------------------------------------------------------

  // WO-42.5: slot counts now driven by variant metafields.
  // See getPackConfigForVariant() below.

  // CRO 2026-09-18: the prior owner's flat "Customization Cost" companion SKUs
  // (POP-CUST-2420/3630/4840/6050 — $24.20 to $60.50 regardless of pack size or how many
  // add-ons you picked) are RETIRED on Andrew's instruction (comet_message_id=4392413).
  // Replaced by real per-pop add-on products charged by the number of BARS covered:
  //   Dip     $0.75/bar  — POP-ADDON-DIP
  //   Topping $0.75/bar  — POP-ADDON-TOP  (requires a dip; topping needs chocolate to stick)
  // Coverage is 100% or 50% of the pack. 50% of an odd pack rounds DOWN in the customer's
  // favour (15 -> 7 bars), because charging for half a bar is not a thing.
  const ADDONS = {
    dip:     { variantId: '46965469642949', sku: 'POP-ADDON-DIP', cents: 75, label: 'Dip' },
    topping: { variantId: '46965470363845', sku: 'POP-ADDON-TOP', cents: 75, label: 'Topping' },
  };

  // Coverage -> multiplier. Keep as data so a 25%/75% tier is a one-line change later.
  const COVERAGE = { '100': 1, '50': 0.5 };

  function barsCovered(packSize, coverage) {
    const mult = COVERAGE[String(coverage)];
    if (!packSize || !mult) return 0;
    return Math.floor(packSize * mult);
  }

  function getPackConfigForVariant(variantId) {
    try {
      const el = document.querySelector('[data-popbar-pack-config]');
      if (!el) return null;
      const map = JSON.parse(el.textContent || '{}');
      const cfg = map[String(variantId)];
      if (!cfg) { console.warn('[popbar-picker] variant', variantId, 'not in config map'); return null; }
      return cfg;
    } catch (e) { console.warn('[popbar-picker] pack config parse error', e); return null; }
  }

  const RESHUFFLE_LIMIT = 1;

  // ---------------------------------------------------------------------------
  // Picker state
  // ---------------------------------------------------------------------------

  class PopbarPicker {
    constructor(root) {
      this.root = root;
      this.productHandle = root.dataset.productHandle;
      this.isMixed = root.dataset.isMixed === 'true';
      this.isGelatoOnly = root.dataset.isGelatoOnly === 'true';
      this.isSorbettoOnly = root.dataset.isSorbettoOnly === 'true';
      this.dippingEnabled = root.dataset.dippingEnabled === 'true';
      this.toppingEnabled = root.dataset.toppingEnabled === 'true';

      this.flavors = [];
      this.selectedHandles = [];
      this.mode = 'selected';
      this.typeFilter = 'all';
      this.reshufflesRemaining = RESHUFFLE_LIMIT;
      this.surpriseSet = [];
      this.slotMax = 5;   // updated when variant changes
      this.packConfig = null;

      this.els = {
        grid:            root.querySelector('[data-flavor-grid]'),
        slots:           root.querySelector('[data-slots]'),
        slotCount:       root.querySelector('[data-slot-count]'),
        slotMax:         root.querySelector('[data-slot-max]'),
        loading:         root.querySelector('[data-loading]'),
        modeButtons:     root.querySelectorAll('[data-mode]'),
        typeToggle:      root.querySelector('[data-type-toggle]'),
        typeFilters:     root.querySelectorAll('[data-type-filter]'),
        surprise:        root.querySelector('[data-surprise]'),
        surprisePreview: root.querySelector('[data-surprise-preview]'),
        surpriseReshuffle: root.querySelector('[data-surprise-reshuffle]'),
        reshuffleCount:  root.querySelector('[data-reshuffle-remaining]'),
        surpriseCommit:  root.querySelector('[data-surprise-commit]'),
        fulfillmentNote: root.querySelector('[data-fulfillment-note]'),
        addons: {
          dipping: root.querySelector('[data-addon="dipping"]'),
          topping: root.querySelector('[data-addon="topping"]'),
          dipCoverage: root.querySelector('[data-addon-coverage="dipping"]'),
          topCoverage: root.querySelector('[data-addon-coverage="topping"]'),
        },
        steps:        root.querySelectorAll('[data-step]'),
        stepHeads:    root.querySelectorAll('[data-step-head]'),
        stepRecap:    root.querySelector('[data-step-recap]'),
        atc:          root.querySelector('[data-popbar-atc]'),
        toppingGroup: root.querySelector('[data-addon-group="topping"]'),
        toppingLock:  root.querySelector('[data-topping-lock]'),
        addonTotal:   root.querySelector('[data-addon-total]'),
        lips: {
          '1st_gelato':   root.querySelector('[data-lip="1st_gelato"]'),
          '1st_sorbetto': root.querySelector('[data-lip="1st_sorbetto"]'),
          '2nd_gelato':   root.querySelector('[data-lip="2nd_gelato"]'),
          '2nd_sorbetto': root.querySelector('[data-lip="2nd_sorbetto"]'),
          custom_product: root.querySelector('[data-lip="custom_product"]'),
          handles:  root.querySelector('[data-lip="handles"]'),
          icons:    root.querySelector('[data-lip="icons"]'),
          mode:     root.querySelector('[data-lip="mode"]'),
          count:    root.querySelector('[data-lip="count"]'),
          fulfillment: root.querySelector('[data-lip="fulfillment"]'),
        },
      };
    }

    async init() {
      await this.loadBootstrap();
      this.attachEvents();
      this.packConfig = getPackConfigForVariant(this.getInitialVariantId());
      this.applyPackConfig();
      this.renderGrid();
      this.syncAddonRules();
      this.updateFulfillmentContext();
      this.applyPreselection();
      this.initSteps();
      this.emit('popbar:picker:ready', { productHandle: this.productHandle });
    }

    async loadBootstrap() {
      const bootstrapEl = this.root.querySelector('[data-flavor-bootstrap]');
      const raw = bootstrapEl.textContent.trim();
      // CRO 2026-09-17: the payload is now inline JSON rendered from the pop_flavor
      // metaobjects. Older themes put an asset URL here — still supported as a fallback.
      let data;
      if (raw.charAt(0) === '[' || raw.charAt(0) === '{') {
        data = JSON.parse(raw);
      } else {
        const res = await fetch(raw);
        data = await res.json();
      }

      // Filter by product type + availability
      let pool = data.filter(f => f.available !== false);
      if (this.isGelatoOnly) pool = pool.filter(f => f.type === 'gelato');
      if (this.isSorbettoOnly) pool = pool.filter(f => f.type === 'sorbetto');
      this.flavors = pool;
      this.els.loading.remove();
    }

    attachEvents() {
      this.els.modeButtons.forEach(btn =>
        btn.addEventListener('click', () => this.setMode(btn.dataset.mode)));
      this.els.typeFilters.forEach(btn =>
        btn.addEventListener('click', () => this.setTypeFilter(btn.dataset.typeFilter)));
      if (this.els.surpriseReshuffle) {
        this.els.surpriseReshuffle.addEventListener('click', () => this.reshuffleSurprise());
      }
      if (this.els.surpriseCommit) {
        this.els.surpriseCommit.addEventListener('click', () => this.commitSurprise());
      }

      // Add-on wiring: dip gates topping, and any change re-prices the running total.
      ['dipping', 'topping'].forEach((k) => {
        this.els.addons[k]?.addEventListener('change', () => this.syncAddonRules());
      });
      [this.els.addons.dipCoverage, this.els.addons.topCoverage].forEach((el) => {
        el?.addEventListener('change', () => this.renderAddonTotal());
      });

      document.addEventListener('variant:change', (e) => {
        const variantId = e.detail?.variant?.id;
        if (variantId) {
          this.packConfig = getPackConfigForVariant(variantId);
          this.applyPackConfig();
          // pack size drives bars covered, so the add-on price changes with the variant
          this.renderAddonTotal();
        }
      });
      this.attachFormAssociation();
    }

    applyPackConfig() {
      const cfg = this.packConfig;
      if (!cfg || !cfg.slots) {
        console.warn('[popbar-picker] no pack config -- hiding picker');
        this.root.hidden = true;
        return;
      }
      this.root.hidden = false;
      this.slotMax = cfg.slots;
      if (this.els.slotMax) this.els.slotMax.textContent = this.slotMax;
      if (this.selectedHandles.length > this.slotMax) {
        this.selectedHandles = this.selectedHandles.slice(0, this.slotMax);
      }
      const surpriseMode = cfg.surprise_mode || 'allowed';
      const modesEl = this.root.querySelector('[role="radiogroup"]');
      if (modesEl) {
        const surpriseBtn = modesEl.querySelector('[data-mode="surprise"]');
        if (surpriseBtn) {
          const show = (surpriseMode === 'allowed');
          surpriseBtn.hidden = !show;
          if (!show && this.mode === 'surprise') this.setMode('selected');
        }
      }
      this.renderSlots();
      // 2026-09-20: slotMax changed with the variant, but the step-1 summary still read the
      // OLD count ("Select 2" on a 30-pop pack). renderSlots() does not rewrite that line;
      // syncSteps() does. Without this the customer is told to pick the wrong number of flavors.
      this.syncSteps();
    }

    getInitialVariantId() {
      const el = document.querySelector('[data-popbar-pack-config]');
      return el ? el.dataset.selectedVariantId : null;
    }

    setMode(mode) {
      this.mode = mode;
      this.els.modeButtons.forEach(b => {
        const active = b.dataset.mode === mode;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-checked', active);
      });
      this.els.grid.hidden = mode === 'surprise';
      this.els.surprise.hidden = mode !== 'surprise';
      if (mode === 'surprise') this.generateSurprise();
      this.els.lips.mode.value = mode;
      this.emit('popbar:picker:mode', { mode });
    }

    setTypeFilter(filter) {
      this.typeFilter = filter;
      this.els.typeFilters.forEach(b =>
        b.classList.toggle('is-active', b.dataset.typeFilter === filter));
      this.renderGrid();
    }

    renderGrid() {
      const pool = this.filteredFlavors();
      this.els.grid.innerHTML = pool.map(f => this.flavorCardHTML(f)).join('');
      this.els.grid.querySelectorAll('[data-flavor-handle]').forEach(el => {
        el.addEventListener('click', () => this.toggleFlavor(el.dataset.flavorHandle));
      });
    }

    filteredFlavors() {
      if (!this.isMixed || this.typeFilter === 'all') return this.flavors;
      return this.flavors.filter(f => f.type === this.typeFilter);
    }

    flavorCardHTML(f) {
      const isSelected = this.selectedHandles.includes(f.handle);
      const dietary = (f.dietary || []).map(d => `<span class="badge badge--${d}">${d}</span>`).join('');
      return `
        <button type="button"
                class="popbar-flavor-card ${isSelected ? 'is-selected' : ''}"
                data-flavor-handle="${f.handle}"
                role="option" aria-selected="${isSelected}">
          <img loading="lazy" src="${f.hero_url}" alt="${f.name}" width="200" height="200">
          <span class="popbar-flavor-card__name">${f.name}</span>
          <span class="popbar-flavor-card__type">${f.type}</span>
          <span class="popbar-flavor-card__badges">${dietary}</span>
        </button>
      `;
    }

    toggleFlavor(handle) {
      const idx = this.selectedHandles.indexOf(handle);
      if (idx > -1) {
        this.selectedHandles.splice(idx, 1);
      } else {
        if (this.selectedHandles.length >= this.slotMax) return;
        this.selectedHandles.push(handle);
      }
      this.renderGrid();
      this.renderSlots();
      this.updateLIPs();
      this.syncSteps();
    }

    renderSlots() {
      const slots = [];
      for (let i = 0; i < this.slotMax; i++) {
        const h = this.selectedHandles[i];
        const f = h && this.flavors.find(x => x.handle === h);
        slots.push(f
          ? `<li data-slot="${i}"><img src="${f.hero_url}" alt="${f.name}" width="60" height="60"><span>${f.name}</span></li>`
          : `<li data-slot="${i}" class="is-empty">+</li>`);
      }
      this.els.slots.innerHTML = slots.join('');
      this.els.slotCount.textContent = this.selectedHandles.length;
    }

    generateSurprise() {
      const pool = this.filteredFlavors();
      if (pool.length < this.slotMax) {
        this.surpriseSet = [...pool];
      } else {
        // Uniform random without replacement
        const shuffled = [...pool].sort(() => Math.random() - 0.5);
        this.surpriseSet = shuffled.slice(0, this.slotMax);
      }
      this.renderSurprisePreview();
    }

    reshuffleSurprise() {
      if (this.reshufflesRemaining <= 0) return;
      this.reshufflesRemaining--;
      this.els.reshuffleCount.textContent = this.reshufflesRemaining;
      this.generateSurprise();
      if (this.reshufflesRemaining === 0) {
        this.els.surpriseReshuffle.disabled = true;
      }
      this.emit('popbar:picker:reshuffle', { remaining: this.reshufflesRemaining });
    }

    renderSurprisePreview() {
      this.els.surprisePreview.innerHTML = this.surpriseSet.map(f =>
        `<li><img src="${f.hero_url}" alt="${f.name}" width="80" height="80"><span>${f.name}</span></li>`
      ).join('');
    }

    commitSurprise() {
      this.selectedHandles = this.surpriseSet.map(f => f.handle);
      this.updateLIPs();
      this.setMode('selected');
      this.renderSlots();
      this.els.lips.mode.value = 'surprise';
    }

    updateLIPs() {
      const gelato   = this.selectedHandles.filter(h => this.flavorByHandle(h)?.type === 'gelato');
      const sorbetto = this.selectedHandles.filter(h => this.flavorByHandle(h)?.type === 'sorbetto');

      // Legacy CSV-in-legacy-key writes
      this.els.lips['1st_gelato'].value   = gelato.length   ? gelato.map(this.humanName.bind(this)).join(', ') : '';
      this.els.lips['1st_sorbetto'].value = sorbetto.length ? sorbetto.map(this.humanName.bind(this)).join(', ') : '';
      this.els.lips['2nd_gelato'].value   = '';   // always empty string, never null
      this.els.lips['2nd_sorbetto'].value = '';

      // Clean forward keys
      this.els.lips.handles.value = this.selectedHandles.join('|');
      this.els.lips.icons.value   = this.selectedHandles.map(h => this.flavorByHandle(h)?.hero_url || '').join('|');
      this.els.lips.count.value   = String(this.selectedHandles.length);

      this.emit('popbar:picker:change', {
        handles: this.selectedHandles,
        count: this.selectedHandles.length,
      });
    }

    // ---- Deep-link preselection (CRO 2026-09-18) ----------------------------
    // A flavor page's "Add this flavor to your pack!" CTA links here with
    // ?flavor=<handle>[|<handle>...]. We seed the picker's slots with those
    // handles so the customer lands on Step 1 with their flavor already chosen.
    //
    // Deliberately forgiving: a handle that is unknown, unavailable, or filtered
    // out by this product's type (a sorbetto handle on the gelato pack) is
    // skipped rather than erroring — a stale link should still open a usable
    // picker, never a broken one. Extra handles past slotMax are dropped.
    applyPreselection() {
      let raw = null;
      try {
        raw = new URLSearchParams(window.location.search).get('flavor');
      } catch (err) {
        return;
      }
      if (!raw) return;

      const wanted = raw.split('|').map(s => s.trim()).filter(Boolean);
      const pool = this.filteredFlavors();
      const valid = [];
      wanted.forEach((h) => {
        if (valid.length >= this.slotMax) return;
        if (valid.includes(h)) return;
        if (pool.some(f => f.handle === h)) valid.push(h);
      });
      if (!valid.length) return;

      this.setMode('selected');
      this.selectedHandles = valid;
      this.renderGrid();
      this.renderSlots();
      this.updateLIPs();
      this.syncSteps();

      // Bring the picker into view — the CTA promised "your pack", so the
      // customer should see the result of their click without hunting for it.
      try {
        this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (err) {
        this.root.scrollIntoView();
      }
      this.emit('popbar:picker:preselected', { handles: valid });
    }

    // ---- Three-step gated layout (CRO 2026-09-18) ---------------------------
    // Step 1 flavors -> step 2 dip -> step 3 topping (optional) -> step 4 add to cart.
    // A locked step is collapsed AND its controls are disabled: dimming alone still
    // leaves the field tabbable, which is how a keyboard user ends up filling in a
    // step the rules say they have not reached yet.
    initSteps() {
      if (!this.els.steps || !this.els.steps.length) return;
      document.body.classList.add('popbar-has-picker');

      this.els.stepHeads.forEach((head) => {
        head.addEventListener('click', () => {
          const n = head.dataset.stepHead;
          const li = this.root.querySelector(`[data-step="${n}"]`);
          if (!li || li.classList.contains('is-locked')) return;
          this.openStep(n);
        });
      });

      if (this.els.atc) {
        this.els.atc.addEventListener('click', () => this.submitViaThemeForm());
      }
      this.syncSteps();
    }

    openStep(n) {
      this.els.steps.forEach((li) => {
        const on = li.dataset.step === String(n);
        li.classList.toggle('is-open', on);
        const head = li.querySelector('[data-step-head]');
        if (head) head.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
    }

    stepState() {
      const flavorsDone = this.selectedHandles.length === this.slotMax && this.slotMax > 0;
      const dipValue = this.els.addons.dipping?.value || '';
      return { flavorsDone, dipValue, dipDone: !!dipValue };
    }

    syncSteps() {
      if (!this.els.steps || !this.els.steps.length) return;
      const { flavorsDone, dipValue, dipDone } = this.stepState();

      const set = (n, { locked, done, summary }) => {
        const li = this.root.querySelector(`[data-step="${n}"]`);
        if (!li) return;
        li.classList.toggle('is-locked', !!locked);
        li.classList.toggle('is-done', !!done);
        if (locked) li.classList.remove('is-open');
        const s = li.querySelector(`[data-step-summary="${n}"]`);
        if (s && summary !== null && summary !== undefined) s.textContent = summary;
        // Controls inside a locked step must not be reachable by keyboard either.
        li.querySelectorAll('select, input, button[data-popbar-atc]').forEach((el) => {
          if (el.hasAttribute('data-popbar-keep-enabled')) return;
          if (locked) {
            el.dataset.popbarLockedDisabled = '1';
            el.disabled = true;
          } else if (el.dataset.popbarLockedDisabled) {
            delete el.dataset.popbarLockedDisabled;
            el.disabled = false;
          }
        });
      };

      set(1, { locked: false, done: flavorsDone,
               summary: flavorsDone
                 ? this.selectedHandles.map(h => this.humanName(h)).join(', ')
                 : `Select ${this.slotMax} — ${this.selectedHandles.length} chosen` });

      set(2, { locked: !flavorsDone, done: dipDone,
               summary: !flavorsDone ? 'Pick your flavors first'
                        : (dipValue ? `${dipValue}, ${this.coverageLabel('dipping')}` : 'No dip') });

      // The dip gate is the product rule, not a UI nicety: a topping has nothing
      // to stick to without chocolate. syncAddonRules() enforces the same thing on
      // the control itself and onAddToCart re-checks it at submit.
      const topValue = this.els.addons.topping?.value || '';
      set(3, { locked: !dipDone, done: !!topValue,
               summary: !dipDone ? 'Pick a dip first'
                        : (topValue ? `${topValue}, ${this.coverageLabel('topping')}` : 'No topping') });

      set(4, { locked: !flavorsDone, done: false,
               summary: flavorsDone ? 'Ready' : 'Pick your flavors first' });

      if (this.els.atc) this.els.atc.disabled = !flavorsDone;
      this.renderRecap();

      // Advance automatically, but never backwards over a step the customer opened.
      const openEl = this.root.querySelector('.popbar-step.is-open');
      if (!openEl) this.openStep(flavorsDone ? (dipDone ? 4 : 2) : 1);
    }

    coverageLabel(kind) {
      const el = kind === 'dipping' ? this.els.addons.dipCoverage : this.els.addons.topCoverage;
      return el && el.value === '50' ? 'half the pack' : 'the whole pack';
    }

    renderRecap() {
      if (!this.els.stepRecap) return;
      const bits = [];
      if (this.selectedHandles.length) {
        bits.push(this.selectedHandles.map(h => this.humanName(h)).join(', '));
      }
      this.addonLines().forEach((l) => {
        bits.push(`${l.cfg.label}: ${l.option} (${l.bars} pops)`);
      });
      this.els.stepRecap.textContent = bits.join(' · ');
    }

    // Step 4's button is a PROXY. Prestige's product-form owns cart submission,
    // section rendering and the drawer; forking that would mean re-implementing all
    // three. We hide the theme button and forward the click to the real form so the
    // existing onAddToCart handler runs exactly as before.
    submitViaThemeForm() {
      const form = document.querySelector('product-form form[action*="/cart/add"]');
      if (!form) { console.warn('[popbar-picker] no product form to proxy'); return; }

      // Guard rails first — same rules onAddToCart enforces, because this button
      // bypasses the form's own submit path when add-ons are present.
      if (this.selectedHandles.length !== this.slotMax) {
        alert(`Please select ${this.slotMax} flavors.`);
        return;
      }
      if (!this.els.addons.dipping?.value && this.els.addons.topping?.value) {
        this.els.addons.topping.value = '';
        this.syncAddonRules();
        alert('Toppings need a dip to stick to — choose a dip first.');
        return;
      }

      const lines = this.addonLines();
      if (lines.length) {
        // Prestige's <product-form> intercepts submit and runs its OWN fetch, so
        // dispatching a submit here never reaches our handler and the add-on lines
        // are silently dropped. Call the atomic path directly instead.
        this.atomicAddWithAddons(form, lines);
        return;
      }
      if (typeof form.requestSubmit === 'function') {
        const native = form.querySelector('[type="submit"]');
        form.requestSubmit(native || undefined);
      } else {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    }

    humanName(handle) {
      const f = this.flavorByHandle(handle);
      return f ? f.name : handle;
    }

    flavorByHandle(handle) {
      return this.flavors.find(f => f.handle === handle);
    }

    updateFulfillmentContext() {
      // Detect Pickeasy fulfillment hint from URL or session
      const params = new URLSearchParams(window.location.search);
      const hint = params.get('fulfillment') || sessionStorage.getItem('popbar:fulfillment') || 'ship';
      this.els.lips.fulfillment.value = hint;
      if (hint === 'pickup' && this.els.fulfillmentNote) {
        this.els.fulfillmentNote.hidden = false;
      }
    }

    // ---- Add-on model: coverage + dip-gates-topping -------------------------

    // A topping cannot exist without a dip — it has nothing to stick to. Enforced in the
    // UI (topping disabled + explanatory note) AND again at submit, so a stale DOM or a
    // keyboard user cannot produce an un-dipped topped order.
    syncAddonRules() {
      const hasDip = !!this.els.addons.dipping?.value;

      if (this.els.addons.topping) {
        this.els.addons.topping.disabled = !hasDip;
        if (!hasDip) this.els.addons.topping.value = '';
      }
      if (this.els.addons.topCoverage) {
        this.els.addons.topCoverage.disabled = !hasDip || !this.els.addons.topping?.value;
      }
      if (this.els.addons.dipCoverage) {
        this.els.addons.dipCoverage.disabled = !hasDip;
      }
      if (this.els.toppingGroup) {
        this.els.toppingGroup.classList.toggle('is-locked', !hasDip);
      }
      if (this.els.toppingLock) {
        this.els.toppingLock.hidden = hasDip;
      }
      this.renderAddonTotal();
      this.syncSteps();
    }

    // Returns [{addon, option, coverage, bars, cents}] for whatever is currently chosen.
    addonLines() {
      const packSize = this.packConfig?.pack_size || 0;
      const lines = [];

      const dipOption = this.els.addons.dipping?.value || '';
      if (dipOption) {
        const cov  = this.els.addons.dipCoverage?.value || '100';
        const bars = barsCovered(packSize, cov);
        if (bars > 0) lines.push({ key: 'dip', cfg: ADDONS.dip, option: dipOption, coverage: cov, bars });
      }

      // topping only when a dip exists — the rule, applied at the data layer too
      const topOption = this.els.addons.topping?.value || '';
      if (dipOption && topOption) {
        const cov  = this.els.addons.topCoverage?.value || (this.els.addons.dipCoverage?.value || '100');
        const bars = barsCovered(packSize, cov);
        if (bars > 0) lines.push({ key: 'topping', cfg: ADDONS.topping, option: topOption, coverage: cov, bars });
      }

      return lines;
    }

    addonTotalCents() {
      return this.addonLines().reduce((sum, l) => sum + (l.bars * l.cfg.cents), 0);
    }

    renderAddonTotal() {
      if (!this.els.addonTotal) return;
      const lines = this.addonLines();
      if (!lines.length) { this.els.addonTotal.textContent = ''; return; }
      const parts = lines.map(l =>
        `${l.cfg.label} on ${l.bars} pop${l.bars === 1 ? '' : 's'} — $${((l.bars * l.cfg.cents) / 100).toFixed(2)}`
      );
      const total = this.addonTotalCents();
      this.els.addonTotal.textContent = `${parts.join(' · ')}  =  +$${(total / 100).toFixed(2)}`;
    }

    countAddons() {
      return this.addonLines().length;
    }

    onAddToCart(e, form) {
      // Validate slot fill
      if (this.selectedHandles.length !== this.slotMax) {
        e.preventDefault();
        alert(`Please select ${this.slotMax} flavors.`);
        return;
      }

      // Hard rule: never let a topped-but-undipped order through, whatever the DOM says.
      if (!this.els.addons.dipping?.value && this.els.addons.topping?.value) {
        e.preventDefault();
        this.els.addons.topping.value = '';
        this.syncAddonRules();
        alert('Toppings need a dip to stick to — choose a dip first.');
        return;
      }

      const lines = this.addonLines();
      if (lines.length) {
        e.preventDefault();
        this.atomicAddWithAddons(form, lines);
      }
      // else: plain pack — Option D FormData picks up the LIP inputs automatically
    }

    async atomicAddWithAddons(form, lines) {
      const formData = new FormData(form);

      try {
        // Record what was ordered on the PACK line, in plain customer-readable words.
        // Internal SKUs and cents deliberately stay out of line-item properties — they
        // surface on customer-facing invoices and quotes.
        const summary = lines
          .map(l => `${l.cfg.label}: ${l.option} (${l.coverage}% — ${l.bars} pops)`)
          .join('; ');
        formData.set('properties[Add-ons]', summary);

        // Legacy "Custom Product" key held the companion SKU's cents value. The flat fee is
        // retired, so it must be an empty string (not null, not a stale number) to keep the
        // HulkApps/WO-21 readers happy without implying a charge.
        if (this.els.lips.custom_product) this.els.lips.custom_product.value = '';
        formData.set('properties[Custom Product]', '');

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: formData,
        });
        if (!res.ok) throw new Error(`Main add failed: ${res.status}`);

        const packVariantId = form.querySelector('[name="id"]')?.value
          || document.querySelector('[name="id"]:not([form])')?.value
          || new URLSearchParams(location.search).get('variant');

        // One cart line per add-on, quantity = bars covered. Quantity x $0.75 means the
        // customer can see exactly what they are paying for, and a 50% pack reads as
        // "Dip (per pop) x 5" rather than an opaque fee.
        for (const l of lines) {
          const addRes = await fetch('/cart/add.js?bridge=skip', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              id: l.cfg.variantId,
              quantity: l.bars,
              properties: {
                'Flavour choice': l.option,
                'Coverage': `${l.coverage}% of pack (${l.bars} pops)`,
                _linked_to: packVariantId,
              },
            }),
          });
          if (!addRes.ok) throw new Error(`${l.cfg.label} add failed: ${addRes.status}`);
        }

        document.dispatchEvent(new CustomEvent('cart:refresh'));
        if (window.theme?.pubsub?.publish) {
          window.theme.pubsub.publish('cart:updated', { source: 'popbar-picker' });
        }

        this.emit('popbar:picker:added', {
          handles: this.selectedHandles,
          packSizeDisplay: this.packConfig?.pack_size || null,
          addons: lines.map(l => ({ type: l.key, option: l.option, coverage: l.coverage, bars: l.bars })),
          addonTotalCents: this.addonTotalCents(),
        });
      } catch (err) {
        console.error('[popbar-picker] add-on add failed', err);
        alert('Something went wrong adding your pops. Please try again.');
      }
    }

    emit(name, detail) {
      document.dispatchEvent(new CustomEvent(name, { detail }));
      if (window.theme?.pubsub?.publish) {
        window.theme.pubsub.publish(name, detail);
      }
    }

    attachFormAssociation() {
      const productFormEl = document.querySelector('product-form');
      const form = productFormEl?.querySelector('form[action*="/cart/add"]');
      if (!form || !form.id) { console.warn('[popbar-picker] ATC form not found'); return; }
      this.root.querySelectorAll('.popbar-flavor-picker__hidden-lips input').forEach(inp => {
        inp.setAttribute('form', form.id);
      });
      form.addEventListener('submit', (e) => {
        if (this.root.hidden) return;
        this.onAddToCart(e, form);
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Bootstrap
  // ---------------------------------------------------------------------------

  function init() {
    document.querySelectorAll('[data-picker]').forEach(root => {
      const picker = new PopbarPicker(root);
      picker.init().catch(err => console.error('[popbar-picker]', err));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

// cache-bust: WO-42.5 fix-c  2026-09-09